import { useQuery } from '@apollo/client'
import type { Image } from '@ur-solutions/react-components'
import {
  Icon,
  ImageViewer,
  Loader,
  usePrompt,
} from '@ur-solutions/react-components'
import { useClickOutside } from '@ur-solutions/react-hooks'
import { Button } from '@/components_deprecated/Button'
import { PlainLink } from '@/components_deprecated/Link'
import { USER_FULL_NAME_QUERY } from '@/modules/users/queries'
import React, { useMemo, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import { ZIndexRange } from '@/types/style'
import { format } from '@/utils/date-fns'
import type { Attachment } from '@/utils/hooks'
import { useAttachmentHandlers } from '@/utils/hooks'
import type { FormTemplateFieldType } from '../types.graphql'

interface ValueCellProps {
  canExpand: boolean
}
const Wrapper = styled.td<ValueCellProps>`
  max-height: 100px;

  & > * {
    white-space: nowrap;
  }

  ${props =>
    props.canExpand &&
    css`
      cursor: cell;

      &:hover {
        background-color: ${props => props.theme.colors.secondary}20;
      }
    `};
`
const Expandable = styled.aside`
  max-width: 600px;
  padding: 1rem;

  background-color: white;
  border-radius: ${props => props.theme.sizes.defaultBorderRadius};
  box-shadow: ${props => props.theme.shadow.default};

  h2 {
    margin: 0 0 0.5rem 0;
  }
  p {
    margin: 0;
    white-space: pre-wrap;
    line-height: 160%;
  }

  ${props => props.theme.media.mobile} {
    max-width: 96vw;
  }
`

const Types = {
  Text: styled.div`
    margin: 0;
    max-width: 300px;

    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
  Number: styled.div`
    text-align: center;
  `,
  Date: styled.div``,
  Checkbox: styled.div`
    text-align: center;
  `,
  User: styled.div``,
}

interface ExpandPromptProps {
  title: string
  text: string
  onClose: (close: null) => void
}
const ExpandPrompt: React.FC<ExpandPromptProps> = ({
  title,
  text,
  onClose,
}) => {
  const ref = useRef<HTMLElement>(null)

  useClickOutside(ref, () => onClose(null), true)

  return (
    <Expandable ref={ref}>
      <h2>{title}</h2>
      <p>{text}</p>
    </Expandable>
  )
}

interface UserNameProps {
  id: string
}

const UserName: React.FC<UserNameProps> = ({ id }) => {
  const { data, loading, error } = useQuery(USER_FULL_NAME_QUERY, {
    variables: { id },
  })

  return !data || loading ? (
    <Loader.Dots size={22} />
  ) : error ? (
    <>N/A</>
  ) : (
    <PlainLink to={`/users/${data.user.id}`} $hoverColor="secondary">
      {data.user.fullName}
    </PlainLink>
  )
}

interface ResponseValueCellProps {
  title: string
  value: string
  type: FormTemplateFieldType
  attachments?: Attachment<string | File>[]
}

export const ResponseValueCell: React.FC<ResponseValueCellProps> = ({
  title,
  value,
  type,
  attachments = [],
}) => {
  const addPrompt = usePrompt()
  const [imageViewerInitialImage, setImageViewerInitialImage] = useState('')
  const [imageViewerAttachments, setImageViewerAttachments] = useState<Image[]>(
    []
  )

  const { handleAttachmentClick } = useAttachmentHandlers(
    attachments,
    setImageViewerInitialImage,
    setImageViewerAttachments
  )

  const render = useMemo(() => {
    if (value === '' && attachments.length < 1) return null
    switch (type) {
      case 'TEXT':
        return <Types.Text>{value}</Types.Text>
      case 'NUMBER':
        return <Types.Number>{value}</Types.Number>
      case 'DATE': {
        if (value === '') return <Types.Date />

        const date = format(new Date(value), 'PP')
        return <Types.Date>{date}</Types.Date>
      }
      case 'CHECKBOX': {
        const checked = value === 'true'

        return (
          <Types.Checkbox>
            <Icon
              icon={checked ? 'check-circle' : 'times-circle'}
              type="solid"
              size="0.8rem"
              color={checked ? 'green' : 'red'}
            />
          </Types.Checkbox>
        )
      }
      case 'USER':
        return (
          <Types.User>
            <UserName id={value} />
          </Types.User>
        )
      case 'ATTACHMENTS': {
        const firstAttachment = attachments[0]
        return (
          <>
            <ImageViewer
              images={imageViewerAttachments}
              zIndex={ZIndexRange.Extra}
              initialImage={imageViewerInitialImage}
              popup
              open={imageViewerAttachments.length > 0}
              noShadow
              showArrows
              background="black !important"
              imageSize="contain"
              imageBackgroundColor="black !important"
              onClose={() => setImageViewerAttachments([])}
            />

            {!!firstAttachment && (
              <Button
                padding="0.5rem"
                height="22px"
                onClick={() => handleAttachmentClick(firstAttachment)}
              >
                Se bilder
              </Button>
            )}
          </>
        )
      }
      default:
        return <Types.Text>{value}</Types.Text>
    }
  }, [
    value,
    attachments,
    type,
    imageViewerAttachments,
    imageViewerInitialImage,
    handleAttachmentClick,
  ])

  async function showExpanded() {
    await addPrompt<null>(resolve => (
      <ExpandPrompt title={title} text={value} onClose={resolve} />
    ))
  }

  const canExpand = type === 'TEXT'

  return (
    <Wrapper canExpand={canExpand} onClick={() => canExpand && showExpanded()}>
      {render}
    </Wrapper>
  )
}
